import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { Container } from '@ag1/ui';

import { useProductListStore } from '@portal/state/store/plpStore';
import { PortableText } from '@portabletext/react';
import { ShopV3Products } from '@portal/types/studio/studio';
import { useMedia } from '@portal/state/context/MediaContext';
import { FormattedShopifyProduct } from '@portal/types/shopify/product.formatted.types';
import { ALL_PRODUCTS_CATEGORY_TAB } from '@portal/shared/studio/ShopListNav/constants';
import UnifiedButton from 'shared/studio/UnifiedButton/UnifiedButton';
import { UnifiedButtonType } from 'shared/studio/UnifiedButton/UnifiedButton.types';
import { CommonSectionProps } from '@portal/shared/studio/types';
import { useIsLoggedInValue } from '@portal/appLayer/auth/useIsLoggedInValue';
import { convertToCamelCase } from '@portal/shared/utils/convertToCamelCase';
import {
  MembersOnlyLoaderKey,
  MembersOnlyProductsSectionName,
  MembersOnlyRefName
} from '@portal/state/store/types';
import { capitalize } from '@portal/shared/utils/capitalize';
import {
  getFlavorGroupNameMetafield,
  getOriginalPriceFootnoteMetafield
} from '@portal/shared/utils/shop/product/metafields';
import { getNumberOfFreeProducts } from '@portal/shared/utils/shop/product/getters';

import { ExploreProductCard } from './ExploreProductCard/ExploreProductCard';
import styles from './ShopListProducts.module.scss';
import { SHOPIFY_METAFIELDS_IDENTIFIERS } from './constants';
import { Skeleton } from './Skeleton/Skeleton';
import { useShopListProducts } from './hooks/useShopListProducts';

interface Props {
  showFreeProducts: boolean;
  showMembersOnlyProducts: boolean;
  sectionCmsData: ShopV3Products & CommonSectionProps;
}

const ShopListMembersOnlyProducts = ({
  showFreeProducts,
  showMembersOnlyProducts,
  sectionCmsData
}: Props) => {
  const isLoggedIn = useIsLoggedInValue();
  const ref = useRef<HTMLDivElement>(null);
  const { atSmall } = useMedia();

  // sanity data destructuring
  const {
    _type: sectionType,
    productsCollectionHandle,
    // IMPORTANT:
    // if "useGlobalStateManagement" is true, "/store/plpStore.ts" will be handling
    // state management
    // if false, this component will be handling state management, and all special
    // features such as filtering & auto-focus section scroll via nav will be disabled
    useGlobalStateManagement,
    config: {
      displaySectionHeader,
      displaySectionTitle,
      displaySectionSubtitle,
      displayShopAllButton
    },
    shopAllButton,
    translations: { exploreSectionTitle, exploreSectionSubtitle, noProductsNotice }
  } = useMemo(() => sectionCmsData, [sectionCmsData]);

  // set up dynamic field names for plpStore based on product collection handle
  const sectionName = convertToCamelCase(
    productsCollectionHandle
  ) as MembersOnlyProductsSectionName;

  const loaderKey = `is${capitalize(sectionName)}SectionLoading` as MembersOnlyLoaderKey;
  const refName = `${sectionName}SectionRef` as MembersOnlyRefName;

  // store state & action destructuring
  // (only used if CMS "useGlobalStateManagement" is true)
  const {
    isLoadingCategoryPage,
    currentActiveCategoryTab,
    isMembersOnlyProductSectionLoading,
    setIsMembersOnlyProductsSectionLoading,
    storeMembersOnlyProducts,
    setStoreMembersOnlyProducts,
    membersOnlyProductsRef,
    setMembersOnlyProductsSectionRef,
    isTransitioning,
    setIsTransitioning
  } = useProductListStore(
    useShallow(({ data, actions }) => ({
      isLoadingCategoryPage: data.loading.isLoadingCategoryPage,
      currentActiveCategoryTab: data.currentActiveCategoryTab,
      isMembersOnlyProductSectionLoading: data.loading.membersOnly[loaderKey],
      setIsMembersOnlyProductsSectionLoading:
        actions.loading.setIsMembersOnlyProductsSectionLoading,
      storeMembersOnlyProducts: data.shopify.membersOnly[sectionName] || [],
      setStoreMembersOnlyProducts: actions.shopify.setMembersOnlyProducts,
      membersOnlyProductsRef: data.pageRefs.membersOnly[refName],
      setMembersOnlyProductsSectionRef: actions.pageRefs.setMembersOnlyProductsSectionRef,
      isTransitioning: data.transition.isTransitioning,
      setIsTransitioning: actions.transition.setIsTransitioning
    }))
  );

  // fetch shopify data
  const {
    data: { products: productsCollection, flavorsMap },
    isPending: isShopifyProductsLoading
  } = useShopListProducts(productsCollectionHandle, SHOPIFY_METAFIELDS_IDENTIFIERS, {
    hideDuplicateProducts: true,
    showMemberPrices: true
  });

  // ----- local section displays states -----
  // (only matters if CMS "useGlobalStateManagement" is true)
  const [isSectionHidden, setIsSectionHidden] = useState(false);

  // ----- local section data states -----
  const [sectionTitle, setSectionTitle] = useState(exploreSectionTitle);
  const [sectionSubtitle, setSectionSubtitle] = useState(exploreSectionSubtitle);
  const [visibleProducts, setVisibleProducts] = useState<FormattedShopifyProduct[]>([]);
  // (these states are used only if CMS "useGlobalStateManagement" is false)
  const [isProductsLoading, setIsProductsLoading] = useState(true);
  const [products, setProducts] = useState<FormattedShopifyProduct[]>([]);

  // determine the loading & data source based on "useGlobalStateManagement" value
  const isSectionLoading = useMemo(() => {
    return useGlobalStateManagement ? isMembersOnlyProductSectionLoading : isProductsLoading;
  }, [useGlobalStateManagement, isMembersOnlyProductSectionLoading, isProductsLoading]);
  const productsData = useMemo(() => {
    return useGlobalStateManagement ? storeMembersOnlyProducts : products;
  }, [useGlobalStateManagement, storeMembersOnlyProducts, products]);

  // listen when shopify data arrives
  useEffect(() => {
    if (isShopifyProductsLoading || !productsCollection) return;

    if (useGlobalStateManagement) {
      if (isMembersOnlyProductSectionLoading) {
        setStoreMembersOnlyProducts(sectionName, productsCollection);
        setVisibleProducts(productsCollection);
        setIsMembersOnlyProductsSectionLoading(loaderKey, false, { isLoggedIn });
      }
    } else if (isProductsLoading) {
      setProducts(productsCollection);
      setVisibleProducts(productsCollection);
      setIsProductsLoading(false);
    }
  }, [
    useGlobalStateManagement,
    isShopifyProductsLoading,
    productsCollection,
    setProducts,
    setVisibleProducts,
    isProductsLoading,
    setIsProductsLoading,
    setStoreMembersOnlyProducts,
    sectionName,
    setIsMembersOnlyProductsSectionLoading,
    isMembersOnlyProductSectionLoading,
    isLoggedIn,
    loaderKey
  ]);

  // set "Explore AG1" section ref on mount
  // (only used if "useGlobalStateManagement" is true)
  useEffect(() => {
    if (useGlobalStateManagement) {
      setMembersOnlyProductsSectionRef(refName, ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-set the productsSectionRef if navigated to other
  // parts of the app with next router and then back
  // ref will be missing when navigating due to template unmounting
  // (only used if "useGlobalStateManagement" is true)
  useEffect(() => {
    if (useGlobalStateManagement) {
      if (!membersOnlyProductsRef?.current && ref) setMembersOnlyProductsSectionRef(refName, ref);
    }
  }, [
    useGlobalStateManagement,
    ref,
    refName,
    membersOnlyProductsRef,
    setMembersOnlyProductsSectionRef
  ]);

  // helper function that updates section visibility, title, subtitle and product list
  // based on the current active category tab
  // (only used if "useGlobalStateManagement" is true)
  const updateSectionState = useCallback(
    ({ dispatchTransitionComplete }: { dispatchTransitionComplete: boolean }) => {
      if (
        currentActiveCategoryTab.slug === ALL_PRODUCTS_CATEGORY_TAB ||
        currentActiveCategoryTab.isMembersOnlyProductSection
      ) {
        // display default section title and subtitle if category is "All"
        setIsSectionHidden(false);
        setSectionTitle(exploreSectionTitle);
        setSectionSubtitle(exploreSectionSubtitle);

        // display all available products if category is "All"
        setVisibleProducts(productsData);

        if (currentActiveCategoryTab.isMembersOnlyProductSection && dispatchTransitionComplete) {
          setTimeout(() => {
            setIsTransitioning(false, {
              aboveSmall: atSmall,
              isMembersOnlyProductSection: currentActiveCategoryTab.isMembersOnlyProductSection,
              refName
            });
          }, 20);
        }
      } else {
        // hide the entire section (via css)
        // if active category is neither "All" or a product sub-category
        setIsSectionHidden(true);
      }
    },
    [
      setIsSectionHidden,
      exploreSectionTitle,
      exploreSectionSubtitle,
      currentActiveCategoryTab,
      productsData,
      setIsTransitioning,
      setVisibleProducts,
      atSmall,
      refName
    ]
  );

  // listen for if the page load is for a category
  // (only used if "useGlobalStateManagement" is true)
  useEffect(() => {
    if (useGlobalStateManagement && isLoadingCategoryPage && !isTransitioning) {
      updateSectionState({ dispatchTransitionComplete: false });
    }
  }, [useGlobalStateManagement, isLoadingCategoryPage, isTransitioning, updateSectionState]);

  // listening for category nav transition
  // (only used if "useGlobalStateManagement" is true)
  useEffect(() => {
    if (useGlobalStateManagement && isTransitioning) {
      updateSectionState({ dispatchTransitionComplete: true });
    }
  }, [useGlobalStateManagement, isTransitioning, updateSectionState]);

  // original price footnote
  const originalPriceFootnote = useMemo(() => {
    const matchedProduct = visibleProducts.find((product) =>
      getOriginalPriceFootnoteMetafield(product)
    );
    return matchedProduct ? getOriginalPriceFootnoteMetafield(matchedProduct)?.value : undefined;
  }, [visibleProducts]);

  // display the products list only if:
  // 1) global state management is not used or,
  // 2) category is "All" or,
  // 3) if another category is active:
  //  - there are more than 1 visible products and,
  //  - they are not all free products
  const showProductsList = useMemo(() => {
    if (!useGlobalStateManagement) return true;
    if (currentActiveCategoryTab.slug === ALL_PRODUCTS_CATEGORY_TAB) return true;
    const numberOfFreeProducts = getNumberOfFreeProducts(visibleProducts);
    return visibleProducts.length > 1 && visibleProducts.length !== numberOfFreeProducts;
  }, [useGlobalStateManagement, currentActiveCategoryTab, visibleProducts]);

  // display skeleton when loading
  // should stop loading when shopify products is fetched and global/local state is loaded
  if (isSectionLoading || isShopifyProductsLoading) {
    return <Skeleton />;
  }

  // do not render section if no products were returned from shopify
  if (!isShopifyProductsLoading && !productsCollection.length) {
    return null;
  }

  return (
    <div
      className={clsx(
        styles.pageContainer,
        styles.exploreProductsSection,
        isTransitioning && styles.transitioning,
        !showProductsList && styles.noProducts,
        isSectionHidden && styles.isHidden
      )}
      ref={ref}>
      <Container narrow>
        {displaySectionHeader && (
          <div
            className={clsx(
              styles.sectionTitleGroup,
              !displaySectionTitle && !displaySectionSubtitle && styles.emptyTitleGroup
            )}>
            <div className={styles.sectionTitleText}>
              {displaySectionTitle && sectionTitle && (
                <h2
                  className={clsx(
                    styles.sectionTitle,
                    !displaySectionSubtitle && styles.emptySubtitle
                  )}>
                  <PortableText value={sectionTitle} />
                </h2>
              )}
              {displaySectionSubtitle && sectionSubtitle && (
                <div className={styles.sectionSubtitle}>
                  <PortableText value={sectionSubtitle} />
                </div>
              )}
            </div>
            {displayShopAllButton && (
              <div className={styles.sectionTitleButtonContainer}>
                <UnifiedButton
                  unifiedButton={{ ...shopAllButton, _type: 'unifiedButton' } as UnifiedButtonType}
                  sectionType={sectionType}
                  className={styles.shopAllButton}
                />
              </div>
            )}
          </div>
        )}
        {showProductsList ? (
          <div className={styles.exploreProductCardsContainer}>
            {visibleProducts.map((productData) => {
              const flavorGroupName = getFlavorGroupNameMetafield(productData)?.value;
              return (
                <ExploreProductCard
                  key={productData.id}
                  showFreeProducts={showFreeProducts}
                  showMembersOnlyProducts={showMembersOnlyProducts}
                  cmsData={sectionCmsData}
                  productData={productData}
                  flavorsListData={flavorGroupName ? flavorsMap[flavorGroupName] : undefined}
                />
              );
            })}
            {displayShopAllButton && (
              <div className={styles.exploreProductsButtonContainer}>
                <UnifiedButton
                  unifiedButton={{ ...shopAllButton, _type: 'unifiedButton' } as UnifiedButtonType}
                  sectionType={sectionType}
                  className={styles.shopAllButton}
                />
              </div>
            )}
          </div>
        ) : (
          noProductsNotice && (
            <div className={styles.noProductsNotice}>
              <PortableText value={noProductsNotice} />
            </div>
          )
        )}
        {originalPriceFootnote && (
          <div className={styles.originalPriceFootnote}>
            <sup className={styles.superscript}>†</sup>
            {originalPriceFootnote}
          </div>
        )}
      </Container>
    </div>
  );
};

export default ShopListMembersOnlyProducts;
